/* eslint-disable no-param-reassign */
/* eslint-disable function-paren-newline */

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import { UncontrolledTooltip, Card, CardBody, CardFooter, Collapse, Row, Col } from 'reactstrap';
import { useFeatures, CardHeader, Button, Loading, useSession, PseudoStates } from '@cordata/buffet';
import { useParams } from 'react-router-dom';
import { Formik, Form } from 'formik';
import {
  forEach,
  chain,
  sortBy,
  zipObject,
  camelCase,
  snakeCase,
  upperFirst,
  debounce,
  filter,
  uniq,
  mapValues,
} from 'lodash';
import { all, spread } from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  addDays,
  endOfMonth,
  endOfYear,
  format,
  startOfMonth,
  startOfYear,
  subMonths,
  subYears,
} from 'date-fns';
import { toast } from 'react-toastify';

import log from '~/utils/event/log';
import { PageLayout } from '~/components';

import {
  authorizedInstance as axios,
  authorizedPyInstance as axiosPy,
  downloadCSV,
} from '~/utils';

import {
  ReportFilter,
  ReportChart,
  ReportTable,
} from '~/components/ConfiguredReport';

import { expandPivotColumns, isPivotColumn } from '~/components/ConfiguredReport/pivot-report-data';

const REPORT_ENDPOINT = 'api/permissable/views';

const defaultDateMap = {
  oneYearAgo: subYears(new Date(), 1),
  twoYearsAgo: subYears(new Date(), 2),
  threeYearsAgo: subYears(new Date(), 3),
  beginningOfCurrentMonth: startOfMonth(new Date()),
  beginningOfCurrentYear: startOfYear(new Date()),
  beginningOfPreviousMonth: startOfMonth(subMonths(new Date(), 1)),
  beginningOfPreviousYear: startOfYear(subYears(new Date(), 1)),
  today: new Date(),
  endOfCurrentMonth: endOfMonth(new Date()),
  endOfCurrentYear: endOfYear(new Date()),
  endOfPreviousMonth: endOfMonth(subMonths(new Date(), 1)),
  endOfPreviousYear: endOfYear(subYears(new Date(), 1)),
};

const transformReportResponse = ({ columns, result }) =>
  result.map(row => zipObject(columns, row));

const getQueryString = (chartOrTable, filters, divisions = []) => {
  const queryStrings = [];

  if (!!divisions && divisions.length) {
    divisions.forEach((d) => {
      queryStrings.push(`division_uid=${d.value}`);
    });
  }

  queryStrings.push('all-data=1');

  forEach(filters, (f) => {
    if (f.type === 'date range') {
      if (f.startDate) {
        queryStrings.push(`${f.columnName}__>>=${format(f.startDate, 'MM/DD/YYYY')}`);
      }

      if (f.endDate) {
        queryStrings.push(`${f.columnName}__<=${format(addDays(f.endDate, 1), 'MM/DD/YYYY')}`);
      }
    } else if (f.type === 'checkbox') {
      if (f.selected === true) {
        // do not apply filter if not-yet-selected or selected-then-unselected
        queryStrings.push(`${f.columnName}=true`);
      }
    } else if (f.type === 'string') {
      if (f.value) {
        queryStrings.push(`${f.columnName}=${f.value}`);
      }
    } else if (f.type === 'badge') {
      if (f.selected) {
        if (f.filterByAllBadges) {
          f.selected.forEach((s) => {
            queryStrings.push(`${f.columnName}__contains_uuid_and=${s.value}`);
          });
        } else {
          f.selected.forEach((s) => {
            queryStrings.push(`${f.columnName}__contains_uuid_or=${s.value}`);
          });
        }
      }
    } else if (f.type === 'boolean radio buttons') {
      if (f.selected === 'true') {
        queryStrings.push(`${f.columnName}=true`);
      } else if (f.selected === 'false') {
        queryStrings.push(`${f.columnName}=false`);

        if (f.radioButtonTreatNullAsFalse) {
          queryStrings.push(`${f.columnName}=null`);
        }
      }
    } else if (f.type === 'number') {
      if (f.value) {
        queryStrings.push(`${f.columnName}__${f.comparisonType.value}=${f.value}`);
      }
    } else if (Array.isArray(f.selected)) {
      if (f.selected.length) {
        f.selected.forEach((s) => {
          queryStrings.push(`${f.columnName}__${f.type === 'custom dropdown' && f.isCaseInsensitive ? 'like' : 'eq'}=${s.value}`);
        });
      }
    } else if (f.selected?.value) {
      queryStrings.push(`${f.columnName}__${f.type === 'custom dropdown' && f.isCaseInsensitive ? 'like' : 'eq'}=${f.selected.value}`);
    }
  });

  if (queryStrings.length) return `?${queryStrings.join('&')}`;

  return '';
};

const ConfiguredReport = ({
  sourceUid,
  options,
  optionsJson = {},
  isWidget,
  ...props
}) => {
  const { current } = useSession();
  const { hasAppIdentity } = useFeatures();
  const { reportName: reportNameFromParams } = useParams();
  const [reportName, setReportName] = useState(reportNameFromParams || props.reportName);
  const [isReportAndNotWidget] = useState(!isWidget);
  const [reportTitle, setReportTitle] = useState(props.reportTitle || 'report');
  const [reportDefinition, setReportDefinition] = useState({});
  const [report, setReport] = useState({ attributes: {} });
  const [filterLayout, setFilterLayout] = useState();
  const [visualizationLayout, setVisualizationLayout] = useState();
  const [filters, setFilters] = useState([]);
  const [charts, setCharts] = useState([]);
  const [tables, setTables] = useState([]);
  const [shouldShowReport, setShouldShowReport] = useState(false);
  const [isRunningReport, setIsRunningReport] = useState(false);
  const [isFilterFormOpen, setIsFilterFormOpen] = useState(!options.hideFilters);
  const [divisionOptions, setDivisionOptions] = useState([]);
  const [selectedDivisions, setSelectedDivisions] = useState([]);
  const [loadingStatus, setLoadingStatus] = useState('loading');
  const [exportStatus, setExportStatus] = useState('done');

  useEffect(() => {
    if (reportNameFromParams) {
      setReportName(reportNameFromParams);
    }
  }, [reportNameFromParams]);

  useEffect(() => {
    if (current) {
      setDivisionOptions(current.attributes.accessibleDivisions.filter(ad =>
        ad.permissions
        && ad.permissions.length > 0
        && ad.permissions.some(p => p.securityPermissionName === 'hasReports'),
      ).map(d => ({
        label: d.divisionName,
        value: d.divisionUid,
      })));
    }
  }, [current]);

  useEffect(() => {
    if (!selectedDivisions.length) {
      setSelectedDivisions(divisionOptions.filter(d => d.value ===
                                                     current.attributes.currentDivisionUid));
    }
  }, [divisionOptions]);

  useEffect(() => {
    setLoadingStatus('loading');
    setShouldShowReport(false);
    setIsRunningReport(false);

    axiosPy
      .get('/api/data/reference/reference-nav-item', {
        params: {
          ref_data_type: 'reports',
          route__like: 'configured',
        },
      })
      .then(r => r.data.data)
      .then(r => r.filter(rr => rr.attributes.route.includes(`:${reportName}`))[0])
      .then(r =>
        setReportTitle(props.reportTitle || (r
          ? r.attributes.navItemLabel
          : reportName)));

    let Api;

    if (sourceUid) {
      Api = axiosPy
        .get(`/api/data/reference/reference-report-configuration/${sourceUid}`);
    } else {
      Api = axiosPy
        .get('/api/data/reference/reference-report-configuration', {
          params: {
            'report-configuration-name__eq': reportName,
          },
        });
    }

    Api
      .then(r => r.data.data)
      .then((r) => {
        const rpt = Array.isArray(r) ? r[0] : r;

        if (!rpt) return;

        const rd = JSON.parse(rpt.attributes.reportConfigurationDefinition);

        setReport(rpt);
        setReportDefinition(rd);
      })
      .catch(() => {
        setLoadingStatus('error');
      });
  }, [sourceUid, reportName]);

  useEffect(() => {
    const l = reportDefinition.layout;

    if (l) {
      const fl = chain(l.filters)
        .map(lf => ({
          ...lf,
          i: lf.i.split('-')[1],
        }))
        .orderBy('x')
        .groupBy('y')
        .values()
        .value();

      const vl = chain(l.visualizations)
        .map(lv => ({
          ...lv,
          type: lv.i.split('-')[0],
          i: lv.i.split('-')[1],
        }))
        .orderBy('x')
        .groupBy('y')
        .values()
        .value();

      setFilterLayout(fl);

      setVisualizationLayout(vl);
    }
  }, [reportDefinition]);

  useEffect(() => {
    const f = reportDefinition.filters || [];
    const filterFetchPromises = [];

    f.forEach((ff) => {
      if (ff.type === 'date range') {
        ff.startDate = (ff.defaultStartDate && defaultDateMap[ff.defaultStartDate])
          ? defaultDateMap[ff.defaultStartDate]
          : subYears(new Date(), 1);
        ff.endDate = (ff.defaultEndDate && defaultDateMap[ff.defaultEndDate])
          ? defaultDateMap[ff.defaultEndDate]
          : new Date();
      } else if (ff.type === 'checkbox') {
        ff.selected = ff.defaultValue;
      } else if (ff.type === 'string') {
        ff.value = ff.defaultValue;
      } else if (ff.type === 'badge') {
        filterFetchPromises.push(
          axios
            .get('/reference-data/reference-badge-groups?include=["reference-badges"]')
            .then(r => r.data.data)
            .then(r => sortBy(r, ['attributes.defaultSortOrder', 'attributes.badgeGroupLabel']))
            .then((r) => {
              ff.filterByAllBadges = false;

              const defaultSelection = [];

              ff.options = sortBy(r, ['attributes.defaultSortOrder', 'attributes.badgeGroupLabel']).map(bg => ({
                label: bg.attributes.badgeGroupLabel,
                options: sortBy(bg.relationships.referenceBadges, ['attributes.defaultSortOrder', 'attributes.badgeLabel']).map((b) => {
                  const option = {
                    label: b.attributes.badgeLabel,
                    value: b.attributes.badgeUid,
                  };

                  if (ff.defaultValue && ff.defaultValue.includes(b.attributes.badgeUid)) {
                    defaultSelection.push(option);
                  }

                  return option;
                }),
              }));

              ff.selected = defaultSelection;
            }),
        );
      } else if (ff.type === 'user') {
        filterFetchPromises.push(
          axios
            .get('/users')
            .then(r => r.data.data)
            .then(r => filter(sortBy(r, ['attributes.lastName', 'attributes.firstName']), 'attributes.displayName'))
            .then((r) => {
              ff.options = r.map((rr) => {
                const value = rr.attributes.displayName;

                return {
                  label: value,
                  value,
                };
              });

              ff.selected = r
                .filter(rr => ff.defaultValue && ff.defaultValue.includes(rr.id))
                .map((rr) => {
                  const value = rr.attributes.displayName;

                  return {
                    label: value,
                    value,
                  };
                });
            }),
        );
      } else if (ff.type === 'provider') {
        ff.selectProps = {
          noOptionsMessage: i => (!i.inputValue.length ? 'Begin typing to search ...' : 'No options'),
          async: true,
          loadOptions: debounce((input, callback) => {
            axios
              .get('providers/mv', {
                params: {
                  q: input,
                  ...ff.reviewersOnly && { reviewer_only: true },
                },
              })
              .then(r => r.data.data)
              .then((r) => {
                callback(r.map(rr => ({
                  label: `${rr.attributes.displayName} (${rr.attributes.providerRoleLabel})`,
                  value: rr.attributes.displayName,
                })));
              });
          },
          200,
          { leading: true }),
        };
      } else if (ff.type === 'facility') {
        filterFetchPromises.push(
          axiosPy
            .get('api/data/public/facility', {
              params: {
                sort: 'default-sort-order,facility-name',
                'all-data': 1,
                'is-in-selections': true,
              },
            })
            .then(r => r.data.data)
            .then((r) => {
              ff.options = r.map(rr => ({
                label: rr.attributes.facilityName,
                value: rr.attributes.facilityName,
              }));

              ff.selected = r
                .filter(rr => ff.defaultValue && ff.defaultValue.includes(rr.id))
                .map(rr => ({
                  label: rr.attributes.facilityName,
                  value: rr.attributes.facilityName,
                }));
            }),
        );
      } else if (ff.type === 'number') {
        ff.value = ff.defaultValue;
      } else if (ff.type === 'custom dropdown') {
        ff.options = ff.customDropdownOptions;

        ff.selected = ff.customDropdownOptions
          .filter(o => ff.defaultValue && ff.defaultValue.includes(o.value));
      } else if (ff.type === 'boolean radio buttons') {
        ff.selected = ff.defaultValue;
      } else if (ff.type === 'other reference data') {
        ff.selectProps = {
          noOptionsMessage: i => (!i.inputValue.length ? 'Begin typing to search ...' : 'No options'),
          async: true,
          loadOptions: debounce((input, callback) => {
            axiosPy
              .get(`/api/data/reference/${ff.refDataModel}`, {
                params: {
                  sort: ff.refDataLabelAttr,
                  [ff.refDataLabelAttr]: input,
                  ...(ff.refDataType && { ref_data_type: ff.refDataType }),
                },
              })
              .then(r => r.data.data)
              .then((r) => {
                callback(r.map(rr => {
                  const value = rr.attributes[camelCase(ff.refDataLabelAttr)];

                  return {
                    label: value,
                    value,
                  };
                }));
              });
          }, 500),
        };

        if (ff.defaultValue) {
          if (!Array.isArray(ff.defaultValue)) {
            filterFetchPromises.push(
              axiosPy
                .get(`/api/data/reference/${ff.refDataModel}/${ff.defaultValue}`)
                .then(r => r.data.data)
                .then((r) => {
                  const value = r.attributes[camelCase(ff.refDataLabelAttr)];

                  ff.selected = {
                    value,
                    label: value,
                  };
                }),
            );
          } else {
            filterFetchPromises.push(
              all(
                ff.defaultValue.map(dv =>
                  axiosPy
                    .get(`/api/data/reference/${ff.refDataModel}/${dv}`)
                    .then(r => r.data.data),
                ),
              )
                .then((r) => {
                  ff.selected = r.map(dv => {
                    const value = dv.attributes[camelCase(ff.refDataLabelAttr)];

                    return {
                      value,
                      label: value,
                    };
                  });
                }),
            );
          }
        }
      }
    });

    all(filterFetchPromises)
      .then(() => {
        setFilters(f);
        setLoadingStatus('success');
      })
      .catch(() => {
        setLoadingStatus('error');
      });
  }, [reportDefinition]);

  useEffect(() => {
    const c = reportDefinition.charts || [];
    const t = reportDefinition.tables || [];

    setCharts(c.map((cc, i) => ({ id: `chart-${i}`, ...cc })));
    setTables(t.map((tt, i) => ({ id: `table-${i}`, pageSize: tt.pageSize || 500, ...tt })));
  }, [reportDefinition]);

  const runReport = () => {
    if (hasAppIdentity) {
      log.create({ action: `Report${upperFirst(camelCase(reportTitle))}` });
    }

    setIsRunningReport(true);

    const queryString = getQueryString({}, filters, selectedDivisions);

    const chartViewMap = [...charts, ...tables].reduce(
      (acc, x) => { acc[x.id] = x.reportView || reportDefinition.reportView; return acc; },
      {},
    );

    const uniqueViews = uniq(Object.values(chartViewMap));

    all(uniqueViews.map(v => axiosPy.get(`${REPORT_ENDPOINT}/${v}${queryString}`)))
      .then(spread((...results) => {
        const viewDataMap = zipObject(uniqueViews, results.map(r => r.data.data));

        const chartDataMap = mapValues(
          chartViewMap,
          view => transformReportResponse(viewDataMap[view]),
        );

        setCharts(charts.map(c => ({
          ...c,
          label: c.title,
          data: chartDataMap[c.id],
        })));

        setTables(tables.map(t => {
          const tableData = chartDataMap[t.id];
          const originalCols = t.originalPivotColumns || t.columns;
          const hasPivots = originalCols && originalCols.some(isPivotColumn);

          if (hasPivots) {
            const pivoted = expandPivotColumns(
              tableData, originalCols, t.groupByColumn,
            );
            return {
              ...t,
              data: pivoted.data,
              columns: pivoted.columns,
              originalPivotColumns: originalCols,
            };
          }

          return { ...t, data: tableData };
        }));

        if (options.hideFilters) {
          setIsFilterFormOpen(!isFilterFormOpen);
        }

        setShouldShowReport(true);
      }))
      .finally(() => {
        setIsRunningReport(false);
      });
  };

  const exportReport = () => {
    setExportStatus('loading');
    toast('Beginning export...', { toastId: 'export', type: toast.TYPE.INFO, autoClose: false });

    tables.forEach((t) => {
      const reportView = t.reportView || reportDefinition.reportView;
      const queryString = getQueryString(t, filters, selectedDivisions);
      const exportColumns = t.originalPivotColumns || t.columns;

      axiosPy
        .post(`/api/permissable/views/${reportView}/csv${queryString}`, {
          columns: exportColumns,
        })
        .then((r) => {
          toast.update('export', { render: 'Export finished!', type: toast.TYPE.SUCCESS, autoClose: 2500 });
          const filename = `${reportTitle} - ${format(new Date(), 'MM-DD-YYYY')}.csv`;
          downloadCSV(r.data.data, filename);
        })
        .catch((e) => {
          toast.update('export', { render: 'Export failed!', type: toast.TYPE.ERROR, autoClose: 2500 });
          console.error('Export failed!', e);
        })
        .finally(() => {
          setTimeout(() => setExportStatus('done'), 2500);
        });
    });
  };

  useEffect(() => {
    if (options.autoRun) runReport();
  }, [filters]);

  if (loadingStatus !== 'success') {
    return <PseudoStates status={loadingStatus} />;
  }

  return (
    <PageLayout
      title={reportTitle}
      titleAction={
        <Select
          className="filter-container"
          value={selectedDivisions}
          onChange={setSelectedDivisions}
          options={divisionOptions}
          isMulti
        />
      }
    >
      <Card className={`border-${optionsJson.color} h-100`}>
        {!isReportAndNotWidget && (
          <CardHeader color={optionsJson.color} inverse={optionsJson.inverse}>
            <div className="d-flex">
              <div className="mr-auto">
                {report.attributes.reportConfigurationLabel}

                {report.attributes.reportConfigurationDescription && (
                  <>
                    <FontAwesomeIcon icon="question-circle" className="ml-2" id={snakeCase(`${reportTitle}-${report.attributes.reportConfigurationName}`)} />
                    <UncontrolledTooltip target={snakeCase(`${reportTitle}-${report.attributes.reportConfigurationName}`)}>{report.attributes.reportConfigurationDescription}</UncontrolledTooltip>
                  </>
                )}
              </div>

              <div className="ml-auto">
                {options.hideFilters && (
                  <Button type="button" size="sm" className="ml-2" color="light" outline={!isFilterFormOpen} onClick={() => setIsFilterFormOpen(!isFilterFormOpen)}>
                    <FontAwesomeIcon icon="cog" />
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
        )}

        <Collapse isOpen={!options.hideFilters || isFilterFormOpen}>
          <Formik
            enableReinitialize
            initialValues={filters}
            validate={setFilters}
            validateOnBlur={false}
            onSubmit={runReport}
          >
            <Form>
              <CardBody>
                {isReportAndNotWidget && report.attributes.reportConfigurationDescription &&
                  <p className="text-muted">{report.attributes.reportConfigurationDescription}</p>
                }

                {filters.length > 0 && (
                  <>
                    {filterLayout && filterLayout.map(row => (
                      <Row form>
                        {row.map(col => (
                          <Col key={col.i} xs={col.w}>
                            <ReportFilter filter={filters[col.i]} {...filters[col.i]} i={col.i} />
                          </Col>
                      ))}
                      </Row>
                    ))}
                  </>
                )}
              </CardBody>

              <CardFooter className="d-flex">
                <div className="ml-auto">
                  {(shouldShowReport && tables.length > 0) && (
                    <Button
                      outline
                      icon="file-export"
                      color="primary"
                      disabled={isRunningReport || exportStatus === 'loading'}
                      onClick={exportReport}
                    >
                      Export
                    </Button>
                  )}

                  <Button
                    type="submit"
                    className="float-right"
                    disabled={isRunningReport}
                  >
                    {/* eslint-disable-next-line */}
                    {isRunningReport ? 'Loading' : (shouldShowReport ? 'Update' : 'Run')} Report
                  </Button>
                </div>
              </CardFooter>
            </Form>
          </Formik>
        </Collapse>

        {shouldShowReport ? (
          <Collapse isOpen={!isFilterFormOpen || isReportAndNotWidget}>
            <CardBody>
              {visualizationLayout
            ? visualizationLayout.map(row => (
              <Row>
                {row.map(col => (
                  <Col key={col.i} xs={col.w}>
                    {col.type === 'chart' && <ReportChart {...charts[col.i]} height={col.h * 60} filters={filters} {...optionsJson} />}
                    {col.type === 'table' && <ReportTable {...tables[col.i]} height={col.h * 60} filters={filters} />}
                  </Col>
                ))}
              </Row>
            ))
            : (
              <>
                {charts?.map(c =>
                  <ReportChart {...c} filters={filters} />,
                )}

                {tables?.map(t =>
                  <ReportTable {...t} />,
                )}
              </>
            )}
            </CardBody>
          </Collapse>
      ) : (isRunningReport) && <Loading /> }
      </Card>
    </PageLayout>
  );
};

ConfiguredReport.propTypes = {
  sourceUid: PropTypes.string,
  options: PropTypes.shape(),
  optionsJson: PropTypes.shape(),
  reportName: PropTypes.string,
  isWidget: PropTypes.bool,
  reportTitle: PropTypes.string,
};

ConfiguredReport.defaultProps = {
  sourceUid: undefined,
  options: {},
  optionsJson: {},
  reportName: '',
  isWidget: false,
  reportTitle: '',
};

export default ConfiguredReport;
